import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Users, CheckCircle2, UserX } from "lucide-react";
import {
  ResponsiveTable,
  ResponsiveTableBody,
  ResponsiveTableCell,
  ResponsiveTableHead,
  ResponsiveTableHeader,
  ResponsiveTableRow,
} from "@/components/ui/responsive-table";
import { EventPositionDTO } from "@/pages/admin/events/types";

interface TablePositionsCardProps {
  eventPositions: EventPositionDTO[];
}

export default function TablePositionsCard({
  eventPositions,
}: TablePositionsCardProps) {
  const [filter, setFilter] = useState<"all" | "filled" | "unfilled">("all");

  const filteredPositions = eventPositions.filter((position) => {
    if (filter === "all") return true;
    const filledCount = position.signedUpUsers?.length || 0;
    const totalRequired = position.employeesRequired * position.quantity;
    const isFilled = filledCount >= totalRequired;
    return filter === "filled" ? isFilled : !isFilled;
  });

  return (
    <Card className="mt-6">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <div className="flex items-center gap-2">
          <Users className="h-5 w-5 text-primary" />
          <CardTitle className="text-xl">Table Positions</CardTitle>
        </div>
        <div className="flex gap-2">
          <Badge
            variant={filter === "all" ? "default" : "outline"}
            onClick={() => setFilter("all")}
            className="cursor-pointer"
          >
            All
          </Badge>
          <Badge
            variant={filter === "filled" ? "default" : "outline"}
            onClick={() => setFilter("filled")}
            className="cursor-pointer"
          >
            Filled
          </Badge>
          <Badge
            variant={filter === "unfilled" ? "default" : "outline"}
            onClick={() => setFilter("unfilled")}
            className="cursor-pointer bg-destructive/10 text-destructive hover:bg-destructive/20"
          >
            Needs Staff
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="rounded-md border">
          <ResponsiveTable>
            <ResponsiveTableHeader>
              <ResponsiveTableRow>
                <ResponsiveTableHead>Table Type</ResponsiveTableHead>
                <ResponsiveTableHead>Quantity</ResponsiveTableHead>
                <ResponsiveTableHead>Staff Per Table</ResponsiveTableHead>
                <ResponsiveTableHead>Total Staff Needed</ResponsiveTableHead>
                <ResponsiveTableHead>Pay Rate</ResponsiveTableHead>
                <ResponsiveTableHead className="text-right">
                  Status
                </ResponsiveTableHead>
              </ResponsiveTableRow>
            </ResponsiveTableHeader>
            <ResponsiveTableBody>
              {filteredPositions.length === 0 ? (
                <ResponsiveTableRow>
                  <ResponsiveTableCell
                    colSpan={6}
                    className="text-center h-24 text-muted-foreground"
                  >
                    No positions found
                  </ResponsiveTableCell>
                </ResponsiveTableRow>
              ) : (
                filteredPositions.map((position) => {
                  const totalStaffNeeded =
                    position.employeesRequired * position.quantity;
                  const staffAssigned = position.signedUpUsers?.length || 0;
                  const isFilled = staffAssigned >= totalStaffNeeded;

                  return (
                    <ResponsiveTableRow
                      key={position.eventPositionId}
                      mobileTitle={position.tableName}
                    >
                      <ResponsiveTableCell
                        className="font-medium"
                        mobileLabel="Table Type"
                        mobilePriority="high"
                      >
                        {position.tableName}
                      </ResponsiveTableCell>
                      <ResponsiveTableCell
                        mobileLabel="Quantity"
                        mobilePriority="medium"
                      >
                        {position.quantity}
                      </ResponsiveTableCell>
                      <ResponsiveTableCell
                        mobileLabel="Staff Per Table"
                        mobilePriority="medium"
                      >
                        {position.employeesRequired}
                      </ResponsiveTableCell>
                      <ResponsiveTableCell
                        mobileLabel="Total Staff Needed"
                        mobilePriority="medium"
                      >
                        {totalStaffNeeded}
                      </ResponsiveTableCell>
                      <ResponsiveTableCell
                        mobileLabel="Pay Rate"
                        mobilePriority="medium"
                      >
                        ${position.employeePay.toFixed(2)}
                        {position.isFlatRate ? " (flat rate)" : "/hr"}
                      </ResponsiveTableCell>
                      <ResponsiveTableCell className="text-right">
                        {isFilled ? (
                          <Badge className="bg-success hover:bg-success/90 flex gap-1 justify-center items-center ml-auto">
                            <CheckCircle2 className="h-3 w-3" />
                            <span>
                              Filled ({staffAssigned}/{totalStaffNeeded})
                            </span>
                          </Badge>
                        ) : (
                          <Badge
                            variant="destructive"
                            className="flex gap-1 justify-center items-center ml-auto"
                          >
                            <UserX className="h-3 w-3" />
                            <span>
                              Needs Staff ({staffAssigned}/{totalStaffNeeded})
                            </span>
                          </Badge>
                        )}
                      </ResponsiveTableCell>
                    </ResponsiveTableRow>
                  );
                })
              )}
            </ResponsiveTableBody>
          </ResponsiveTable>
        </div>
      </CardContent>
    </Card>
  );
}
